/**
 * Global number and currency formatter for Eyvo eProcurement.
 * Reads decimal_places from system settings (stored in AuthContext / systemSettings).
 * Usage:
 *   const { fmt, fmtCurrency, fmtQty } = useFormatter();
 *   fmt(1234.5)           → "1,234.50"  (uses system decimal places)
 *   fmtCurrency(1234.5, 'GBP')  → "1,234.50 GBP"
 *   fmtQty(5)             → "5"         (no decimals for quantities)
 */
import { useContext } from 'react';
import AuthContext from '../contexts/AuthContext';
import ExpenditureLabelsContext from '../contexts/ExpenditureLabelsContext';

// Standalone formatter (no hook required — pass decimalPlaces explicitly)
export function formatNumber(value, decimalPlaces = 2) {
  const num = parseFloat(value);
  if (isNaN(num)) return '0.' + '0'.repeat(decimalPlaces);
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  }).format(num);
}

export function formatCurrency(value, currency = '', decimalPlaces = 2) {
  const formatted = formatNumber(value, decimalPlaces);
  return currency ? `${formatted} ${currency}` : formatted;
}

export function formatPercent(value, decimalPlaces = 2) {
  return `${formatNumber(value, decimalPlaces)}%`;
}

// React hook — reads decimal_places from ExpenditureLabelsContext (system-wide setting)
export function useFormatter() {
  const ctx = useContext(AuthContext);
  const expCtx = useContext(ExpenditureLabelsContext);
  const dp = parseInt(expCtx?.decimalPlaces ?? ctx?.systemSettings?.decimal_places ?? 2);
  const currency = ctx?.systemSettings?.system_currency || 'USD';

  return {
    /** Format a financial number with configured decimal places */
    fmt: (value) => formatNumber(value, dp),

    /** Format with currency code appended */
    fmtCurrency: (value, overrideCurrency) => formatCurrency(value, overrideCurrency ?? currency, dp),

    /** Format a percentage */
    fmtPct: (value) => formatPercent(value, dp),

    /** Format a quantity (whole number, no decimals) */
    fmtQty: (value) => formatNumber(value, 0),

    /** Format a rate/exchange (4 decimal places always) */
    fmtRate: (value) => formatNumber(value, 4),

    /** The current decimal places setting */
    decimalPlaces: dp,

    /** The system currency */
    systemCurrency: currency,
  };
}

// Convenience: format for display in table cells — handles null/undefined gracefully
export function fmtNum(value, decimalPlaces = 2) {
  if (value === null || value === undefined || value === '') return '—';
  return formatNumber(value, decimalPlaces);
}
